import { useTheme, type ThemeChoice } from '../hooks/useTheme';
import { useBahasa } from '../i18n';

const LABEL: Record<ThemeChoice, string> = {
  light: 'Light theme',
  dark: 'Dark theme',
  system: 'Match system',
};

function Icon({ choice }: { choice: ThemeChoice }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (choice === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    );
  }
  if (choice === 'dark') {
    return (
      <svg {...common}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

/**
 * Tiga state: light → dark → system (§G6).
 * Target klik 44px (§G7). Status diumumkan lewat aria-label yang ikut berubah,
 * jadi pemakai screen reader tahu tema aktif tanpa melihat ikonnya.
 *
 * `bentuk="baris"` dipakai di kaki sidebar yang terbuka (§G9): di sana ia
 * berdiri sendiri di antara item bernama, dan ikon telanjang tanpa label akan
 * jadi satu-satunya kendali di seluruh navigasi yang harus ditebak. Labelnya
 * menyebut tema yang sedang **aktif**, bukan yang akan dituju — itu yang
 * ditanya orang saat melihatnya.
 */
export function ThemeToggle({ bentuk = 'ikon' }: { bentuk?: 'ikon' | 'baris' }) {
  const { choice, cycle } = useTheme();
  const { t } = useBahasa();
  const nama = t(LABEL[choice]);

  if (bentuk === 'baris') {
    return (
      <button
        type="button"
        onClick={cycle}
        aria-label={t('{tema}. Click to change theme.', { tema: nama })}
        className="flex min-h-tap w-full items-center gap-3 rounded-md px-3 text-sm text-content transition-colors hover:bg-subtle"
      >
        {/* Kotak selebar ikon navigasi (20px) supaya labelnya sebaris
            dengan label item menu di atasnya; ikon tema sendiri 18px. */}
        <span className="flex w-5 shrink-0 justify-center">
          <Icon choice={choice} />
        </span>
        <span className="truncate" aria-hidden="true">
          {nama}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={t('{tema}. Click to change theme.', { tema: nama })}
      title={nama}
      className="grid min-h-tap min-w-tap place-items-center rounded-md border border-line text-content transition-colors hover:bg-subtle"
    >
      <Icon choice={choice} />
    </button>
  );
}
